import Button from '@mui/material/Button';
import format from 'date-fns/format';
import { Fragment, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import type { PartialSaveOptions } from 'payments/types/form';
import {
  useAccessToken,
  useLocationId,
  useSlug
} from 'reservations/account/providers/ConfigProvider';
import { NEXTABLE_ERROR_CODE } from 'reservations/base/constants/nextable';
import { useErrorSnackbar } from 'reservations/base/hooks/useSnackbar';
import { useLoadingSpinner } from 'reservations/base/providers/LoadingSpinnerProvider';
import { useModal } from 'reservations/base/providers/ModalProvider';
import { useCustomRouter } from 'reservations/base/providers/RouterProvider';
import {
  useBookingChecklessPayment,
  useBookingDetails,
  useBookingPaymentMethod
} from 'reservations/booking/providers/BookingProvider';
import type {
  HandleErrorOptions,
  HandleUpdateBookingSuccessOptions
} from 'reservations/booking/types';
import { useIsSubjectToCancellationFee } from 'reservations/feePolicy/hooks';
import { useUpdateBookingMutation } from 'reservations/form/queries/useUpdateBookingMutation';
import { RequiredPaymentModify } from 'reservations/payments/components/RequiredPayment';
import { usePaymentProviderSubmission } from 'reservations/payments/hooks/usePaymentProviderSubmission';

const UpdateReservationButton = () => {
  const isSubjectToCancellationFee = useIsSubjectToCancellationFee();
  const ButtonWrapper = isSubjectToCancellationFee
    ? RequiredPaymentModify
    : Fragment;

  return (
    <ButtonWrapper>
      <InternalUpdateReservationButton
        isSubjectToCancellationFee={isSubjectToCancellationFee}
      />
    </ButtonWrapper>
  );
};

interface InternalUpdateReservationButtonProps {
  isSubjectToCancellationFee: boolean;
}

const InternalUpdateReservationButton = ({
  isSubjectToCancellationFee
}: InternalUpdateReservationButtonProps) => {
  const { handleHasLoaded, handleIsLoading } = useLoadingSpinner();
  const { handleNavigation } = useCustomRouter();
  const { handleSubmit } = useFormContext();
  const locationId = useLocationId();
  const accessToken = useAccessToken();
  const slug = useSlug();
  const enqueueErrorSnackbar = useErrorSnackbar();
  const currentPaymentMethod = useBookingPaymentMethod();
  const { bookingId, dateAndTime } = useBookingDetails();
  const { optInChecklessPayments, tipPercentage } =
    useBookingChecklessPayment();
  const [availability, savedPaymentMethod] = useWatch({
    name: ['availability', 'savedPaymentMethod']
  });
  const previousReservation = useMemo(
    () => format(new Date(dateAndTime), "h:mmaa 'on' EEEE, LLL d"),
    [dateAndTime]
  );
  const { close } = useModal();
  const onSuccess = () => {
    close();
    handleHasLoaded();
  };

  const handlePaymentProviderSubmission =
    usePaymentProviderSubmission(onSuccess);

  // istanbul ignore next
  const handleSuccess = ({
    navUrl,
    booking
  }: HandleUpdateBookingSuccessOptions) => {
    // TODO: Ask Robert what implications there are if we try to update the payment method
    // when we're updating the reservation. Will it come back as pending again?

    const shouldSubmitPaymentProviderForm =
      isSubjectToCancellationFee &&
      savedPaymentMethod !== currentPaymentMethod?.id;

    if (shouldSubmitPaymentProviderForm) {
      const payload: PartialSaveOptions = {
        locationId,
        bookingId: booking.bookingId,
        email: booking.email,
        firstName: booking.firstName,
        lastName: booking.lastName,
        phone: booking.phone,
        phoneCode: booking.phoneCode,
        // TODO: Update once nextable is able to handle modifications
        // that change whether a booking is subject to cancellation or not
        shouldConfirmCardWithNextable: false,
        optInChecklessPayments,
        tipPercentage,
        ...(savedPaymentMethod && { savedPaymentMethod })
      };

      handlePaymentProviderSubmission(payload);
    } else {
      onSuccess();

      if (navUrl) {
        handleNavigation(navUrl);
      }
    }
  };

  // istanbul ignore next
  const handleError = ({
    navUrl,
    code,
    forceReload = false
  }: HandleErrorOptions) => {
    handleHasLoaded();

    if (
      code === NEXTABLE_ERROR_CODE.NO_AVAILABILITY ||
      NEXTABLE_ERROR_CODE.TIME_SLOT_UNAVAILABLE
    ) {
      enqueueErrorSnackbar(
        'UPDATE_BOOKING_ERROR_SNACKBAR',
        `${availability} is no longer available. You're still confirmed
            for ${previousReservation}`
      );
    }

    if (navUrl || forceReload) {
      handleNavigation(navUrl, forceReload);
    }
  };

  const mutation = useUpdateBookingMutation({
    slug,
    bookingId,
    apiKey: accessToken,
    handleSuccess,
    handleError
  });

  const onSubmit = (data: Record<string, any>) => {
    handleIsLoading('Updating your reservation...');
    mutation.mutate(data);
  };

  return (
    <Button
      data-testid="update-reservation-button-v1"
      onClick={handleSubmit(onSubmit, handleHasLoaded)}
    >
      Update Reservation
    </Button>
  );
};

export default UpdateReservationButton;
